import { useMemo, useState } from 'react';

export const PERIOD_MODE = {
    all: 'all',
    month: 'month',
    day: 'day',
    range: 'range',
};

export function currentMonthKey(d = new Date()) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function isoDate(d = new Date()) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function monthBounds(monthKey) {
    const [y, m] = monthKey.split('-').map(Number);
    const from = new Date(y, m - 1, 1);
    const to = new Date(y, m, 0);
    return {
        from: isoDate(from),
        to: isoDate(to),
    };
}

export function periodLabel({ periodMode, month, singleDay, from, to }) {
    if (periodMode === PERIOD_MODE.all) return 'All time';
    if (periodMode === PERIOD_MODE.month && month) {
        return new Date(`${month}-01`).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
    }
    if (periodMode === PERIOD_MODE.day && singleDay) {
        return new Date(`${singleDay}T12:00:00`).toLocaleDateString('en-IN', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        });
    }
    if (periodMode === PERIOD_MODE.range) {
        if (from && to) return `${from} → ${to}`;
        if (from) return `From ${from}`;
        if (to) return `Until ${to}`;
        return 'Pick date range';
    }
    return '';
}

export function buildDateApiParams({ periodMode, month, singleDay, from, to }) {
    if (periodMode === PERIOD_MODE.all) return {};
    if (periodMode === PERIOD_MODE.month && month) return { month };
    if (periodMode === PERIOD_MODE.day && singleDay) return { from: singleDay, to: singleDay };
    if (periodMode === PERIOD_MODE.range) {
        const params = {};
        if (from) params.from = from;
        if (to) params.to = to;
        return params;
    }
    return {};
}

export function useKpiDateFilter(initial = {}) {
    const [periodMode, setPeriodMode] = useState(initial.periodMode || PERIOD_MODE.all);
    const [month, setMonth] = useState(initial.month || currentMonthKey());
    const [singleDay, setSingleDay] = useState(initial.singleDay || isoDate());
    const [from, setFrom] = useState(initial.from || '');
    const [to, setTo] = useState(initial.to || '');
    const [activePreset, setActivePreset] = useState(initial.periodMode ? 'month' : 'all');

    const apiParams = useMemo(
        () => buildDateApiParams({ periodMode, month, singleDay, from, to }),
        [periodMode, month, singleDay, from, to],
    );

    const label = useMemo(
        () => periodLabel({ periodMode, month, singleDay, from, to }),
        [periodMode, month, singleDay, from, to],
    );

    const applyPreset = (key) => {
        const now = new Date();
        if (key === 'today') {
            const d = isoDate(now);
            setPeriodMode(PERIOD_MODE.day);
            setSingleDay(d);
            setActivePreset(key);
        } else if (key === 'yesterday') {
            const y = new Date(now);
            y.setDate(y.getDate() - 1);
            setPeriodMode(PERIOD_MODE.day);
            setSingleDay(isoDate(y));
            setActivePreset(key);
        } else if (key === 'week') {
            const start = new Date(now);
            start.setDate(start.getDate() - 6);
            setPeriodMode(PERIOD_MODE.range);
            setFrom(isoDate(start));
            setTo(isoDate(now));
            setActivePreset(key);
        } else if (key === 'all') {
            setPeriodMode(PERIOD_MODE.all);
            setFrom('');
            setTo('');
            setActivePreset(key);
        } else if (key === 'month') {
            setPeriodMode(PERIOD_MODE.month);
            setMonth(currentMonthKey());
            setActivePreset(key);
        } else if (key === 'day') {
            setPeriodMode(PERIOD_MODE.day);
            setActivePreset('day');
        } else if (key === 'range') {
            const b = monthBounds(month || currentMonthKey());
            setPeriodMode(PERIOD_MODE.range);
            setFrom(b.from);
            setTo(b.to);
            setActivePreset('range');
        }
    };

    const reset = () => {
        setPeriodMode(PERIOD_MODE.all);
        setMonth(currentMonthKey());
        setSingleDay(isoDate());
        setFrom('');
        setTo('');
        setActivePreset('all');
    };

    return {
        periodMode,
        setPeriodMode,
        month,
        setMonth,
        singleDay,
        setSingleDay,
        from,
        setFrom,
        to,
        setTo,
        activePreset,
        setActivePreset,
        applyPreset,
        apiParams,
        label,
        reset,
    };
}
